import { z } from 'zod';

import { PerformanceTier, TierKeys } from '../types/performanceTier';

export type HostMetricsSettings = {
  // In Time Response Events
  POINTS_FOR_IN_TIME_RESPONSE: number;
  POINTS_FOR_NOT_IN_TIME_RESPONSE: number;
  IN_TIME_RESPONSE_MAX_SCORE: number;
  IN_TIME_RESPONSE_MIN_SCORE: number;
  IN_TIME_RESPONSE_LIMIT_HOURS: number;

  // Review Events
  POSITIVE_REVIEW: number;
  NEGATIVE_REVIEW: number;
  REVIEW_MAX_SCORE: number;
  REVIEW_MIN_SCORE: number;

  // Level Thresholds
  LEVEL_1_THRESHOLD: number;
  LEVEL_2_THRESHOLD: number;
  LEVEL_3_THRESHOLD: number;
};

export enum ScoringEventEnum {
  IN_TIME_RESPONSE_EVENT = 'IN_TIME_RESPONSE_EVENT',
  REVIEW_EVENT = 'REVIEW_EVENT',
}

const ScoringEventTypeSchema = z.nativeEnum(ScoringEventEnum);
export type ScoringEventType = z.infer<typeof ScoringEventTypeSchema>;

export enum ActionSentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export enum DeltaSentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL_NEGATIVE = 'neutral-negative',
  NEUTRAL_POSITIVE = 'neutral-positive',
}

export const ScoringEventMetadataSchema = z.object({
  participant: z
    .object({
      id: z.string().uuid(),
      firstName: z.string(),
      lastName: z.string(),
      photo: z.string().optional(),
    })
    .optional(),
  actionSentiment: z.nativeEnum(ActionSentiment),
  deltaSentiment: z.nativeEnum(DeltaSentiment),
});
export type ScoringEventMetadata = z.infer<typeof ScoringEventMetadataSchema>;

export const ScoringEventSchema = z.object({
  id: z.string(),
  type: ScoringEventTypeSchema,
  points: z.number(),
  cappedScoreDelta: z.number(),
  conversationId: z.string().nullable(),
  hostScoreId: z.string(),
  accountId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  metadata: ScoringEventMetadataSchema,
});
export type ScoringEvent = z.infer<typeof ScoringEventSchema>;

export const HostScoreSchema = z.object({
  id: z.string().uuid(),
  type: ScoringEventTypeSchema,
  accountId: z.string().uuid(),
  cappedScore: z.number(),
  totalScore: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  level: z.number(),
});
export type HostScore = z.infer<typeof HostScoreSchema>;

export type HostScoreChartApiResponse = {
  scores: Array<{
    date: string;
    score: {
      cappedScore: number;
      totalScore: number;
    };
  }>;
};

export type HostScoreDeltaApiResponse = {
  accountId: string;
  cappedScore: number;
  totalScore: number;
};

export type HostScoreChartData = Array<{
  name: string;
  score: number;
}>;

export const getHostMetricsLevelLabelAndIcon = (level: number) => {
  const levelKey = TierKeys[level];
  switch (levelKey) {
    case 'basic':
      return { label: PerformanceTier[levelKey], icon: '' };
    case 'bronze':
      return { label: PerformanceTier[levelKey], icon: '🥉' };
    case 'silver':
      return { label: PerformanceTier[levelKey], icon: '🥈' };
    case 'gold':
      return { label: PerformanceTier[levelKey], icon: '🥇' };
    default:
      console.error(`Unknown score level: ${level}`);
      return { label: level, icon: undefined };
  }
};

export const getHostScoringTypeLabel = (type: ScoringEventType) => {
  switch (type) {
    case 'IN_TIME_RESPONSE_EVENT':
      return 'Timely Response';
    case 'REVIEW_EVENT':
      return 'Review';
    default:
      console.error(`Unknown ScoringEventType: ${type}`);
      return type;
  }
};

export const getPercentageWithinTier = (
  score: number,
  tier: number,
  type: ScoringEventType,
  settings: HostMetricsSettings,
) => {
  const {
    LEVEL_1_THRESHOLD,
    LEVEL_2_THRESHOLD,
    LEVEL_3_THRESHOLD,
    IN_TIME_RESPONSE_MAX_SCORE,
    REVIEW_MAX_SCORE,
  } = settings;

  const MAX_SCORE =
    type === 'IN_TIME_RESPONSE_EVENT'
      ? IN_TIME_RESPONSE_MAX_SCORE
      : REVIEW_MAX_SCORE;

  switch (tier) {
    case 0:
      return (score / LEVEL_1_THRESHOLD) * 100;
    case 1:
      return (
        ((score - LEVEL_1_THRESHOLD) /
          (LEVEL_2_THRESHOLD - LEVEL_1_THRESHOLD)) *
        100
      );
    case 2:
      return (
        ((score - LEVEL_2_THRESHOLD) /
          (LEVEL_3_THRESHOLD - LEVEL_2_THRESHOLD)) *
        100
      );
    case 3:
      return (
        ((score - LEVEL_3_THRESHOLD) / (MAX_SCORE - LEVEL_3_THRESHOLD)) * 100
      );
    default:
      console.error(`Unknown tier: ${tier}`);
      return 0;
  }
};
